import { Button, ButtonVariant } from '@florencelabs/button';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import {
  formatPhone,
  phoneToTelUri,
} from '@provider-directory/components/phone-number';
import { cn } from '@provider-directory/functions/cn';
import { useIsMobile } from '@provider-directory/hooks/useBreakpoints';
import { ConversionContext } from '@provider-directory/lib/analytics/conversions';
import type {
  ProviderResource,
  ProvidersSearchCriteria,
} from '@provider-directory/types/providers.contracts';

interface CallToInquireButtonProps
  extends Pick<ConversionContext, 'linkContextPage' | 'linkContextComponent'> {
  provider: ProviderResource;
  phoneNumber: string;
  variant?: ButtonVariant;
  className?: string;
  searchCriteria?: Partial<ProvidersSearchCriteria>;
}

export const CallToInquireButton = ({
  provider,
  phoneNumber,
  variant = 'clientTonal',
  className,
  searchCriteria,
  linkContextPage,
  linkContextComponent,
}: CallToInquireButtonProps): ReactElement => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const isMobile = useIsMobile();

  const handleCallToInquire = (e: React.MouseEvent): void => {
    e.preventDefault();

    const telUri = phoneToTelUri(phoneNumber);
    analytics.calledEvent({
      provider,
      phoneNumber,
      linkContextPage,
      linkContextComponent,
      searchCriteria,
      clickText: phoneNumber,
      clickURL: telUri,
    });

    window.open(telUri);
  };

  return (
    <Button
      variant={variant}
      size={'small'}
      className={cn('w-full', className)}
      onClick={handleCallToInquire}
      icon={'call_fill'}
      iconPosition={'left'}
      testId={`provider-${provider.npi}-call-to-inquire-btn`}
    >
      {isMobile
        ? t('search.callToScheduleShort')
        : t('search.callToSchedule', {
            phone: formatPhone(phoneNumber, { format: 'abridged' }),
          })}
    </Button>
  );
};
